const TemporaryFileInputId: string = '81f916ff-3604-4c31-ad41-677e4dad8925';

/**
 * Adds an file input element to the DOM and calls click() on it.
 *
 * Eventlisteners for onchange and onerror are connected to that element and will resolve the function with null or the elements FileList
 *
 * Support:
 * - filetype restriction is not supported in Edge
 * - multiple parameter is ignored in Android < 4.0
 *
 * @param accept filters the list of possible files based on the file mimetype (does not work in all browser)
 * @param multiple allows selection of multiple files (default false)
 */
export function getFileList(accept: string, multiple: boolean = false): Promise<FileList | null> {
    return new Promise<FileList | null>((resolve: (value: FileList | null) => void) => {
        try {
            document.getElementById(TemporaryFileInputId)?.remove();

            const fileInput: HTMLInputElement = document.createElement('input');

            fileInput.id = TemporaryFileInputId;
            fileInput.accept = accept;
            fileInput.style.display = 'none';
            fileInput.type = 'file';
            fileInput.multiple = multiple;

            fileInput.onerror = (): void => {
                resolve(null);
            };

            (fileInput as {oncancel?: VoidFunction} & HTMLInputElement).oncancel = (): void => {
                resolve(null);
            };

            fileInput.onchange = (event: Event): void => {
                resolve((event.target as HTMLInputElement)?.files ?? null);
            };

            document.body.appendChild(fileInput);

            fileInput.click();
        } catch {
            resolve(null);
        }
    });
}

export type UniqueFilenameGenerator = (filename: string, extension: string) => string;
export function getUniqueFilenameGenerator(): (filename: string, extension: string) => string {
    const cache: Map<string, number> = new Map();
    return (filename: string, extension: string): string => {
        const key: string = `${filename}.${extension}`;
        const count: number | undefined = cache.get(key);
        if (count == null) {
            cache.set(key, 0);

            return key;
        }

        cache.set(key, count + 1);
        return `${filename}_${count + 1}.${extension}`;
    };
}